// MARK: NPM Modules
import React, { useState } from "react";
import styled from "styled-components";
// MARK: Redux
import { RootState } from "reduxFunctionality/index";
import { useSelector } from "react-redux";
// MARK: Types
import NavigationBarProps from "types/globals/navigationBar/navigationBarProps";
import ReduxStyleguide from "types/redux/styleguide";
import NavigationItem from "types/globals/navigationBar/navigationItem";
// MARK: Components
import Image from "components/common/media/image";
import Link from "components/common/links/standard";
import Typography from "components/common/typography";
import { Container, LogoContainer, Identity, Nav } from "../shared";
import SearchIcon from "components/common/search/searchIcon";
import SecondaryMenuItem from "./secondaryMenuItem";
// MARK: Functionality
import URI from "utils/common/uri";
import determineActiveHeader from "../shared/determineActiveHeader";
import generateIdForMenuItem from "../shared/generateIdForMenuItem";
import CSSStyles from "enums/cssStyles";
import CSSColors from "enums/cssColors";

// MARK: Styled Components
interface FlexProps {
  width: number;
}
const Flex = styled.div<FlexProps>`
  display: flex;
  width: 100%;
  height: 100%;
  margin: auto;
  justify-content: space-between;
  align-items: center;
`;

const Menu = styled.ul`
  display: flex;
  height: 100%;
  justify-content: flex-end;
  align-items: center;

  margin-block-start: 0em;
  margin-block-end: 0em;
  margin-inline-start: 0em;
  margin-inline-end: 0em;
  padding-inline-start: 0px;
`;

interface NavigationItemProps {
  height: number;
  marginright: number;
}

const NavigationListItem = styled.li<NavigationItemProps>`
  cursor: pointer;
  display: flex;
  height: ${({ height }) => height}px;
  margin-right: ${({ marginright }) => marginright}px;
  justify-content: center;
  align-items: center;
  :hover {
    text-decoration: underline;
  }
`;

interface SecondaryMenuProps {
  padding: number;
  top: number;
  left: number;
  width: number;
}

const SecondaryMenu = styled.section<SecondaryMenuProps>`
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  top: ${({ top, padding }) => top - padding}px;
  left: 0;
  right: 0;
  padding-left: ${({ left }) => left}px !important;
  padding-right: ${({ left, width }) =>
    "calc(100vw - " + left + "px - " + width + "px)"};
  background-color: transparent;
`;

interface SecondaryMenuListProps {
  backgroundcolor: string;
}

const SecondaryMenuList = styled.ul<SecondaryMenuListProps>`
  background-color: ${({ backgroundcolor }) => backgroundcolor};
  margin-block-start: 0em;
  margin-block-end: 0em;
  margin-inline-start: 0em;
  margin-inline-end: 0em;
  padding-inline-start: 0px;
`;

// MARK: React Component
// Styleguide & Datapoints
const getDatapoints = (styleguide: ReduxStyleguide) => {
  const { colors } = styleguide;
  const { primary, background } = colors;
  return {
    color: primary,
    background: background,
  };
};
// Component
const DesktopNavigationBar = ({ data, activeRoute }: NavigationBarProps) => {
  // MARK: React Redux Variables
  const styleguide: ReduxStyleguide = useSelector(
    (state: RootState) => state.styleguide
  );
  // MARK: State Variables
  const [activeItem, setActiveItem] = useState<NavigationItem | undefined>(
    undefined
  );
  // MARK: Variables
  const { maximumBodyWidth, navigationBarHeight, margin, gutter, containerWidth } = styleguide;
  const { color, background } = getDatapoints(styleguide);
  const floatingMenuWidth: number = 250;
  // MARK: Functionality

  const generateNavigationLink = (
    active: boolean,
    navigationItem: NavigationItem,
    header: string,
    marginRight: number
  ) => {
    return (
      <NavigationListItem
        id={generateIdForMenuItem(header)}
        key={generateIdForMenuItem(header)}
        itemProp="name"
        role="menuitem"
        marginright={marginRight}
        height={navigationBarHeight}
        onMouseEnter={() => {
          switch (navigationItem.pages.length) {
            case 1:
              setActiveItem(undefined);
              break;
            default:
              setActiveItem(navigationItem);
              break;
          }
        }}
      >
        <Link
          title={header}
          to={
            navigationItem.pages[0].uri === "__home__"
              ? "/"
              : URI(navigationItem.pages[0].uri)
          }
        >
          {header === "search" ? (
            <SearchIcon allowsInteraction={true} />
          ) : (
            <Typography
              className={CSSStyles.navigation}
              type={"span"}
              color={active ? color : color}
              hoverColor={color}
              textDecoration={active ? "underline" : ""}
            >
              {header}
            </Typography>
          )}
        </Link>
      </NavigationListItem>
    );
  };

  const navigationItems = () => {
    const { navigationItems } = data;
    var navItems: Array<any> = [];

    navigationItems.forEach((item, index) => {
      const active = determineActiveHeader(item, activeRoute);
      const header = item.header.toLowerCase();
      const marginRight = index === navigationItems.length - 1 ? 0 : gutter;
      navItems.push(generateNavigationLink(active, item, header, marginRight));
    });

    return navItems;
  };

  const renderFloatingMenu = () => {
    const menu: any = [];

    if (activeItem && typeof window !== "undefined") {
      const boundingBox = window.document
        .getElementById(generateIdForMenuItem(activeItem.header))
        ?.getBoundingClientRect();
      if (boundingBox) {
        menu.push(
          <SecondaryMenu
            key={"secondary-menu-" + activeItem.header}
            top={navigationBarHeight}
            left={boundingBox.x + boundingBox.width / 2 - floatingMenuWidth / 2}
            padding={gutter}
            width={floatingMenuWidth}
            onMouseLeave={() => {
              setActiveItem(undefined);
            }}
          >
            <SecondaryMenuList
              role={"menu"}
              itemScope
              itemType={"https://schema.org/SiteNavigationElement"}
              backgroundcolor={background}
              onMouseLeave={() => {
                setActiveItem(undefined);
              }}
            >
              {renderFloatingMenuItems()}
            </SecondaryMenuList>
          </SecondaryMenu>
        );
      }
    }
    return menu;
  };

  const renderFloatingMenuItems = () => {
    const menuItems: any = [];
    if (activeItem) {
      activeItem.pages.forEach((item, index) => {
        menuItems.push(
          <SecondaryMenuItem
            key={"secondary-menu-item-" + index + "-" + activeItem.header}
            item={item}
            activeRoute={activeRoute}
          />
        );
      });
    }
    return menuItems;
  };

  // MARK: Render
  return (
    <Container
      mobile={false}
      height={navigationBarHeight + "px"}
      margin={margin}
      backgroundcolor={background}
      maxWidth={maximumBodyWidth}
    >
      <Flex width={containerWidth}>
        <Identity
          to="/"
          aria-label={"Tap to go to the home page."}
          dimension={navigationBarHeight}
        >
          <LogoContainer>
            <Image
              src={"/delasignBlack.webp"}
              alt={"delasign"}
              height={"100%"}
            />
          </LogoContainer>
        </Identity>
        <Nav role={"navigation"} margin={margin}>
          <Menu
            role={"menu"}
            itemScope
            itemType={"https://schema.org/SiteNavigationElement"}
          >
            {navigationItems()}
          </Menu>
        </Nav>
      </Flex>
      {renderFloatingMenu()}
    </Container>
  );
};
export default DesktopNavigationBar;
